// src/components/PreOnboardedOrganizationsTable.js
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Snackbar,
  CircularProgress,
  Modal,
  Alert,
} from "@mui/material";
import { makeStyles } from '@mui/styles';
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";

// Import the necessary actions
import {
  getPreOnBoardedOrganizations,
  resendOrgOnBoardActionLink,
  clearAllMessages,
} from "../../../store/actions";

import {
  deleteProspectiveSellRecord,
  clearAllDeleteProspectiveSellRecordMessages,
} from "../../../store/organization/deleteProspectiveSellRecordSlice";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "whitesmoke",
  },
  activeButton: {
    color: "white", // change this to the desired active color
    marginLeft: "auto", // This will push the link to the right
    textDecoration: "none", // Remove text decoration from the link
  },
  logo: {
    maxWidth: 160,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    overflowY: "auto" /* Enable vertical scrolling */,
    maxHeight: "100vh" /* Set a maximum height to limit scrolling */,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  buttonPanel: {
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-end",
    marginTop: "2rem",
  },
}));

const PreOnboardedOrganizationsTable = ({ userRole }) => {
  const dispatch = useDispatch();

  // State variables for dialog, snackbar, etc.
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [activationToken, setActivationToken] = useState(null);

  const classes = useStyles();

  // Selectors to get the necessary state from the Redux store
  const preOnBoardedOrganizations = useSelector(
    (state) => state.organization.preOnBoardedOrganizations || {}
  );
  const isGettingPreOnBoardedOrganizations = useSelector(
    (state) => state.organization.isGettingPreOnBoardedOrganizations
  );
  const resendOrgOnBoardActivationLinkMessage = useSelector(
    (state) => state.organization.resendOrgOnBoardActivationLinkMessage
  );
  const resendOrgOnBoardActivationLinkError = useSelector(
    (state) => state.organization.resendOrgOnBoardActivationLinkError
  );

  const isResendingOrgOnBoardActivationLink = useSelector(
    (state) => state.organization.isResendingOrgOnBoardActivationLink
  );

  const deleteSuccessMessage = useSelector(
    (state) =>
      state.deleteProspectiveSellRecordSlice.deleteProspectiveSellRecordMessage
  );
  const deleteErrorMessage = useSelector(
    (state) =>
      state.deleteProspectiveSellRecordSlice.deleteProspectiveSellRecordError
  );
  const isDeleting = useSelector(
    (state) =>
      state.deleteProspectiveSellRecordSlice.isDeletingProspectiveSellRecord
  );

  // console.log("deleteSuccessMessage: ", deleteSuccessMessage);
  // console.log("deleteErrorMessage: ", deleteErrorMessage);
  // console.log("isDeleting: ", isDeleting);

  // Fetch pre-onboarded organizations when the component mounts
  useEffect(() => {
    dispatch(getPreOnBoardedOrganizations());
  }, [dispatch]);

  // Handle the activation link resend
  const handleActivationClick = (rowData) => {
    setConfirmationOpen(true);
    setActivationToken(rowData.token);
    // dispatch(resendOrgOnBoardActionLink(rowData.token));
  };

  const handleConfirmationModalClose = () => {
    setConfirmationOpen(false);
    setActivationToken(null);
  };

  const handleYes = () => {
    setConfirmationOpen(false);
    dispatch(resendOrgOnBoardActionLink(activationToken));
    // dispatch(resendOrgOnBoardActionLink("TEST-123"));
  };

  const handleNo = () => {
    setConfirmationOpen(false);
    setActivationToken(null);
  };

  // Handle dialog opening and closing
  const handleOpenDeleteDialog = (token) => {
    setSelectedToken(token);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedToken(null);
  };

  // Dispatch delete action and close dialog
  const handleConfirmDelete = () => {
    // dispatch(deleteProspectiveSellRecord("TEST-123"));
    dispatch(deleteProspectiveSellRecord(selectedToken));
    handleCloseDeleteDialog();
  };

  // Handle Snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    dispatch(clearAllDeleteProspectiveSellRecordMessages());
  };

  // Show Snackbar when success or error messages change
  useEffect(() => {
    if (deleteSuccessMessage) {
      setSnackbarMessage(deleteSuccessMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      console.log("Refreshing pre onboarded organizations...");
      dispatch(getPreOnBoardedOrganizations());
    } else if (deleteErrorMessage) {
      setSnackbarMessage(deleteErrorMessage || "Error deleting record");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [deleteSuccessMessage, deleteErrorMessage, dispatch]);

  // Show Snackbar when success or error messages change for activation token
  useEffect(() => {
    if (resendOrgOnBoardActivationLinkMessage) {
      setSnackbarMessage(resendOrgOnBoardActivationLinkMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else if (resendOrgOnBoardActivationLinkError) {
      setSnackbarMessage(
        resendOrgOnBoardActivationLinkError.message ||
          "Error sending activation email"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [
    resendOrgOnBoardActivationLinkMessage,
    resendOrgOnBoardActivationLinkError,
    dispatch,
  ]);

  const preOnBoardOrgsColumns = [
    {
      field: "delete",
      headerName: "Delete",
      width: 80,
      renderCell: (params) => (
        <div>
          {userRole.includes("Admin") && (
            <>
              <IconButton
                variant="contained"
                style={{ color: "red" }}
                onClick={() => handleOpenDeleteDialog(params.row.token)}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </div>
      ),
    },
    { field: "firstName", headerName: "First Name", width: 100 },
    { field: "lastName", headerName: "Last Name", width: 100 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    { field: "email", headerName: "Email", width: 300 },
    {
      field: "edit",
      headerName: "Activation Token",
      width: 150,
      renderCell: (params) => (
        <IconButton
          variant="contained"
          color="primary"
          onClick={() => handleActivationClick(params.row)}
          disabled={isResendingOrgOnBoardActivationLink}
        >
          <SendIcon />
        </IconButton>
      ),
    },
    { field: "workAddress", headerName: "Work Address", width: 150 },
    { field: "zipcode", headerName: "Zip Code", width: 80 },
    { field: "city", headerName: "City", width: 80 },
    { field: "province", headerName: "Province", width: 80 },
    { field: "country", headerName: "Country", width: 80 },
    { field: "company", headerName: "Company", width: 150 },
    { field: "token", headerName: "Token", width: 150 },
    { field: "maxacounts", headerName: "Max Counts", width: 100 },
    { field: "maxusers", headerName: "Max Users", width: 100 },
    { field: "complianceEnum", headerName: "Compliance Enum", width: 250 },
    { field: "initiationdt", headerName: "Initiation Date", width: 200 },
    { field: "expirationdt", headerName: "Expiration Date", width: 200 },
  ];

  return (
    <div>
      {isGettingPreOnBoardedOrganizations || isDeleting ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <>
          <DataGrid
            rows={Object.values(preOnBoardedOrganizations)}
            columns={preOnBoardOrgsColumns}
            pageSize={5}
            components={{
              Toolbar: GridToolbar,
            }}
            disableSelectionOnClick
            autoHeight
            disableColumnMenu
            disableColumnSelector
            getRowId={(row) => row.id}
            density="compact"
          />

          <Dialog
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            aria-labelledby="delete-confirmation-dialog"
          >
            <DialogTitle id="delete-confirmation-dialog">
              Confirm Delete
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this record?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="secondary"
                disabled={isDeleting}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}

      {/* Modal for confirmation of Disabling  addon*/}
      {isConfirmationOpen && (
        <Modal
          className={classes.modal}
          open={isConfirmationOpen}
          onClose={handleConfirmationModalClose}
        >
          <div className={classes.paper}>
            <h3>Are you sure you want to send Activation Email?</h3>
            <div className={classes.buttonPanel}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#f44336",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                  },
                }}
                onClick={handleNo}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
                onClick={handleYes}
              >
                Yes
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PreOnboardedOrganizationsTable;
